import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { transitionSession } from '@/features/sessions/services/sessionTransitions';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Pause, Play, Square, Camera, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';

function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return [h, m, s].map((v) => String(v).padStart(2, '0')).join(':');
}

type BannerSession = {
  id: string;
  client_id: string | null;
  client_name: string | null;
  status_v2: string;
  started_at: string | null;
  elapsed_seconds: number;
  timer_started_at: string | null;
  clients: { first_name: string; last_name: string } | null;
};

type ReservedMaterial = {
  id: string;
  product_id: string;
  quantity: number;
  products: { name: string; stock: number } | null;
};

export function ActiveSessionBanner() {
  const navigate    = useNavigate();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [tick, setTick]                  = useState(0);
  const [showModal, setShowModal]        = useState(false);
  const [closingNotes, setClosingNotes]  = useState('');
  const [photoFile, setPhotoFile]        = useState<File | null>(null);
  const [photoPreview, setPhotoPreview]  = useState<string | null>(null);
  const [adjustedQtys, setAdjustedQtys] = useState<Record<string, number>>({});
  const [closing, setClosing]            = useState(false);
  const [showPostModal, setShowPostModal] = useState(false);
  const [closedSessionId, setClosedSessionId]   = useState<string | null>(null);
  const [closedClientId, setClosedClientId]     = useState<string | null>(null);

  const { data: session } = useQuery<BannerSession | null>({
    queryKey: ['active-banner-session'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;
      const { data } = await (supabase as any)
        .from('sessions')
        .select('id, client_id, client_name, status_v2, started_at, elapsed_seconds, timer_started_at, clients(first_name, last_name)')
        .eq('owner_id', user.id)
        .in('status_v2', ['in_progress', 'paused'])
        .order('started_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      return data ?? null;
    },
    staleTime: 0,
    refetchInterval: 5_000,
  });

  const { data: reservedMaterials = [] } = useQuery<ReservedMaterial[]>({
    queryKey: ['session-reserved-materials', session?.id],
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from('stock_movements')
        .select('id, product_id, quantity, products(name, stock)')
        .eq('session_id', session!.id)
        .eq('movement_type', 'reserved');
      return data ?? [];
    },
    enabled: !!session,
  });

  // Seed adjusted quantities when modal opens
  useEffect(() => {
    if (!showModal) return;
    const init: Record<string, number> = {};
    reservedMaterials.forEach((m) => { init[m.id] = m.quantity; });
    setAdjustedQtys(init);
  }, [showModal, reservedMaterials]);

  // Tick every second when in_progress
  useEffect(() => {
    if (!session || session.status_v2 !== 'in_progress') return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [session?.id, session?.status_v2]);

  const elapsed = useMemo(() => {
    if (!session) return 0;
    const base = session.elapsed_seconds || 0;
    if (session.status_v2 === 'paused') return base;
    const origin = session.timer_started_at
      ? new Date(session.timer_started_at)
      : new Date(session.started_at ?? Date.now());
    return base + Math.floor((Date.now() - origin.getTime()) / 1000);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, tick]);

  const isRunning = session?.status_v2 === 'in_progress';

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['active-banner-session'] });
    queryClient.invalidateQueries({ queryKey: ['sessions'] });
    if (session) queryClient.invalidateQueries({ queryKey: ['session', session.id] });
    queryClient.invalidateQueries({ queryKey: ['products'] });
  };

  const handlePauseResume = async () => {
    try {
      if (isRunning) {
        await transitionSession(session.id, 'paused', { elapsed_seconds: elapsed });
      } else {
        await transitionSession(session.id, 'in_progress');
      }
      invalidate();
    } catch (err: any) {
      toast.error(err?.message ?? 'Erreur');
    }
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleClose = async () => {
    setClosing(true);
    try {
      // 1. Upload photo — optional, non-fatal
      let photoAfterUrl: string | null = null;
      if (photoFile) {
        try {
          const { data: { user } } = await supabase.auth.getUser();
          const ext  = photoFile.name.split('.').pop() ?? 'jpg';
          const path = `${user!.id}/${session.id}/after.${ext}`;
          const { error: uploadError } = await supabase.storage
            .from('client-photos')
            .upload(path, photoFile, { upsert: true });
          if (!uploadError) {
            const { data: urlData } = supabase.storage
              .from('client-photos')
              .getPublicUrl(path);
            photoAfterUrl = urlData.publicUrl;
          }
        } catch {
          // Photo is optional — continue regardless
        }
      }

      // 2. Update quantities and mark stock as consumed
      const { data: movements } = await (supabase as any)
        .from('stock_movements')
        .select('id, product_id, quantity')
        .eq('session_id', session.id)
        .eq('movement_type', 'reserved');

      if (movements?.length) {
        for (const m of movements as Array<{ id: string; product_id: string; quantity: number }>) {
          const actualQty = adjustedQtys[m.id] ?? m.quantity;
          await (supabase as any)
            .from('stock_movements')
            .update({ movement_type: 'consumed', quantity: actualQty })
            .eq('id', m.id);
          const { data: product } = await (supabase as any)
            .from('products')
            .select('stock')
            .eq('id', m.product_id)
            .single();
          if (product) {
            await (supabase as any)
              .from('products')
              .update({ stock: Math.max(0, (product.stock || 0) - actualQty) })
              .eq('id', m.product_id);
          }
        }
      }

      // 3. Transition: [in_progress|paused] → closing → completed
      if (session.status_v2 !== 'closing') {
        await transitionSession(session.id, 'closing');
      }
      await transitionSession(session.id, 'completed', {
        closing_notes:   closingNotes || null,
        photo_after_url: photoAfterUrl,
        elapsed_seconds: elapsed,
      });

      const doneSessionId = session.id;
      const doneClientId  = session.client_id;

      invalidate();
      toast.success('Session terminée');
      setShowModal(false);
      setClosingNotes('');
      setPhotoFile(null);
      setPhotoPreview(null);
      setClosedSessionId(doneSessionId);
      setClosedClientId(doneClientId);
      setShowPostModal(true);
    } catch (err: any) {
      toast.error(err?.message ?? 'Erreur lors de la clôture');
    } finally {
      setClosing(false);
    }
  };

  return (
    <>
      {session && (<>
        <div className="w-full border-b border-amber-500/30 bg-amber-500/10 px-4 py-2 flex items-center gap-3 text-sm">
          <button
            className="font-semibold hover:text-primary hover:underline underline-offset-2 truncate max-w-[160px]"
            onClick={() => session.client_id && navigate(`/clients/${session.client_id}`)}
            title="Ouvrir la fiche client"
          >
            {session.clients
              ? `${session.clients.first_name} ${session.clients.last_name}`.trim()
              : session.client_name || 'Client'}
          </button>

          <span className="flex-1 text-center font-mono font-bold tracking-widest">
            {formatDuration(elapsed)}
          </span>

          <div className="flex items-center gap-2 shrink-0">
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8 border border-amber-500/40 hover:bg-amber-500/20"
              onClick={handlePauseResume}
              title={isRunning ? 'Pause' : 'Reprendre'}
            >
              {isRunning ? <Pause size={14} /> : <Play size={14} />}
            </Button>

            <Button
              size="sm"
              variant="destructive"
              className="gap-1.5 h-8"
              onClick={() => setShowModal(true)}
            >
              <Square size={12} />
              Terminer
            </Button>
          </div>
        </div>

        <Dialog open={showModal} onOpenChange={(open) => { if (!closing) setShowModal(open); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Clôturer la session — {session.client_name}</DialogTitle>
            <DialogDescription className="sr-only">Formulaire de clôture de session en cours</DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            <div className="space-y-1.5">
              <Label>Notes de fin de session</Label>
              <Textarea
                placeholder="Déroulement, observations, prochaine étape…"
                value={closingNotes}
                onChange={(e) => setClosingNotes(e.target.value)}
                rows={3}
              />
            </div>

            <div className="space-y-1.5">
              <Label>
                Photo après{' '}
                <span className="text-muted-foreground font-normal">(optionnel)</span>
              </Label>
              {photoPreview ? (
                <div className="relative w-full aspect-video rounded-lg overflow-hidden border">
                  <img src={photoPreview} alt="Aperçu" className="w-full h-full object-cover" />
                  <button
                    className="absolute top-2 right-2 bg-black/50 rounded-full p-1 hover:bg-black/70"
                    onClick={() => { setPhotoFile(null); setPhotoPreview(null); }}
                  >
                    <X size={14} className="text-white" />
                  </button>
                </div>
              ) : (
                <button
                  className="w-full border-2 border-dashed border-muted-foreground/30 rounded-lg py-5 flex flex-col items-center gap-2 text-muted-foreground hover:border-primary/50 hover:text-primary transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Camera size={20} />
                  <span className="text-sm">Ajouter une photo</span>
                </button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={handlePhotoChange}
              />
            </div>

            {reservedMaterials.length > 0 && (
              <div className="space-y-2">
                <Label>Matériaux utilisés</Label>
                <p className="text-xs text-muted-foreground">
                  Ajustez les quantités réellement consommées.
                </p>
                <div className="divide-y rounded-xl border overflow-hidden">
                  {reservedMaterials.map((m) => (
                    <div key={m.id} className="flex items-center gap-3 px-4 py-2.5">
                      <span className="flex-1 text-sm">{m.products?.name ?? '—'}</span>
                      <Input
                        type="number"
                        min={0}
                        className="w-20 h-8 text-sm text-center"
                        value={adjustedQtys[m.id] ?? m.quantity}
                        onChange={(e) =>
                          setAdjustedQtys((q) => ({ ...q, [m.id]: Number(e.target.value) }))
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2 pt-2">
            <Button variant="outline" onClick={() => setShowModal(false)} disabled={closing}>
              Annuler
            </Button>
            <Button variant="destructive" onClick={handleClose} disabled={closing} className="gap-2">
              {closing && <Loader2 size={14} className="animate-spin" />}
              {closing ? 'Clôture en cours…' : 'Confirmer et terminer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </>)}

      {/* ── Modal post-clôture — toujours rendu pour survivre au session=null ── */}
      <Dialog open={showPostModal} onOpenChange={setShowPostModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Session terminée — que faire ensuite ?</DialogTitle>
            <DialogDescription className="sr-only">Actions disponibles après la clôture de la session</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 pt-1">
            <Button
              className="w-full gap-2 bg-green-600 hover:bg-green-700 text-white"
              onClick={() => { setShowPostModal(false); navigate(`/invoices/new?sessionId=${closedSessionId}${closedClientId ? `&clientId=${closedClientId}` : ''}`); }}
            >
              Générer la facture
            </Button>
            <Button
              variant="secondary"
              className="w-full"
              onClick={() => { setShowPostModal(false); navigate(`/sessions/${closedSessionId}`); }}
            >
              Voir la fiche session
            </Button>
            {closedClientId && (
              <Button
                variant="secondary"
                className="w-full"
                onClick={() => { setShowPostModal(false); navigate(`/clients/${closedClientId}`); }}
              >
                Retourner à la fiche client
              </Button>
            )}
            <Button variant="ghost" className="w-full" onClick={() => setShowPostModal(false)}>
              Fermer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
